import React, { useEffect, useState } from 'react';
import { OverlayConstants } from '../../../Constants';
import { connect, useDispatch } from 'react-redux';
import useErrorState from '../../../utils/errors/useErrorState';
import { addErrorMessage, setOverlay, showErrors } from '../../../redux/slices/appStateSlice';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import InputQuestion from '../../questions/InputQuestion';
import DateQuestion from '../../questions/DateQuestion';
import config from '../../../temp/config';
import { useInternalNavigate } from '../../../utils/navigate/useNavigate';
import { errorMessages as constErrorMessages } from '../../../utils/errors/constants';
import applyRetrievedQuote from '../../../utils/prospectData/applyRetrievedQuote';
import { RETRIEVAL_OUTCOMES } from '../../../utils/prospectData/validateRetrievedQuote';
import useRecaptcha from '../../../utils/recaptcha/useRecaptcha';
import { pushServiceDownError } from '../../../utils/analytics/datalayer';
import PropTypes from 'prop-types';

const blockingErrorTypes = [constErrorMessages.EMPTY];

const RetrieveQuote = props => {
  const { fields, show, retrieveQuoteNumber, dateOfBirth, errorMessages, onError } = props;
  const dispatch = useDispatch();
  const [errors, setErrors] = useErrorState(
    'retrieve-quote-form',
    'retrieve-quote',
    fields,
    onError
  );
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [invalidError, setInvalidError] = useState('');
  const navigate = useInternalNavigate();
  useRecaptcha();
  const hideOverlay = () => {
    document.body.classList.remove('vds-body-locked');
    dispatch(setOverlay(null));
  };

  const getValidationMessage = field => errors[field] || '';

  useEffect(() => {
    if (displayErrors) {
      if (handleErrors()) {
        setInvalidError('');
      }
    }
    for (const field in errors) {
      if (errors.hasOwnProperty(field)) {
        if (blockingErrorTypes.includes(errors[field].type)) {
          setEnableSubmit(false);
          return;
        }
      }
    }
    setEnableSubmit(true);
  }, [errors]);

  const renderState = () => {
    const getErrorMessage = () => {
      if (typeof invalidError === 'string') {
        return invalidError;
      }
      return invalidError?.message || '';
    };

    return (
      <div className="vds-slide-out-drawer__content" id="slideout-retrievequote-form">
        <Text tag="h3" className="vds-slide-out-drawer__subheading" field={fields.formTitle} />
        <form
          className="vds-form"
          id="slideout-retrievequote"
          data-testid="retrievequote-form"
          onSubmit={handleSubmit}
          noValidate
        >
          {renderInputs()}
          <div className="vds-form__group vds-form__group--d2c-actions">
            <div className="vds-form__group">
              <div className="vds-form__validation-message vds-form__validation-message--invalid">
                <span className="vds-form__validation-text" data-testid="error-message">
                  {getErrorMessage()}
                </span>
              </div>
            </div>
            <button
              className={`vds-button vds-button--fluid${
                enableSubmit ? '' : ' vds-button--disabled'
              }`}
              style={{ cursor: 'pointer', pointerEvents: 'unset' }}
              id="retrievequote-submit"
              data-testid="retrievequote-submit"
              type="submit"
            >
              <Text field={fields.submitButtonText} />
            </button>
          </div>
        </form>
        <div className="vds-info-panel vds-margin-bottom-spacing-5" data-testid="info-panel">
          <RichText
            tag="h2"
            className="vds-type-display-2b vds-margin-bottom-spacing-2"
            data-testid="info-panel-title"
            field={fields.introTitle}
          />
          <dl className="vds-margin-bottom-spacing-2">
            <Text field={fields.introStrapLine} />
            <dd>
              <a
                className="InfinityNumber clickable cbslideoutphnumber"
                href={fields.contactNumber?.value}
                data-testid="contact-number-link"
              >
                {fields.contactNumberText?.value}
              </a>
            </dd>
          </dl>
          <RichText field={fields.introFooter} />
        </div>
      </div>
    );
  };

  const renderInputs = () => {
    if (fields.questions?.length >= 1) {
      const [quoteNumber, dob] = fields.questions;
      document.body.classList.add('vds-body-locked');
      return (
        <div>
          <InputQuestion
            key={'retrieveQuoteQuestion-quoteNumber'}
            rendering={{ uid: 'retrieveQuote-quoteNumber' }}
            fields={quoteNumber.fields}
            displayErrors={displayErrors}
            onError={errorMessage => {
              if (errorMessage) {
                setErrors(prev => ({ ...prev, [quoteNumber.fields.name?.value]: errorMessage }));
              } else {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[quoteNumber.fields.name?.value];
                  return newErrors;
                });
              }
            }}
          />
          <DateQuestion
            key={'retrieveQuoteQuestion-dob'}
            rendering={{ uid: 'retrieveQuote-dob' }}
            fields={dob.fields}
            displayErrors={displayErrors}
            onError={errorMessage => {
              if (errorMessage) {
                setErrors(prev => ({ ...prev, [dob.fields.name?.value]: errorMessage }));
              } else {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[dob.fields.name?.value];
                  return newErrors;
                });
              }
            }}
          />
        </div>
      );
    }
  };

  const retrieveQuoteData = async data => {
    try {
      const result = await applyRetrievedQuote(data, dispatch);

      if (result.outcome === RETRIEVAL_OUTCOMES.PRINCIPAL_INVALID) {
        if (fields.serviceDownPage?.value?.href) {
          navigate(fields.serviceDownPage.value.href);
        } else {
          setInvalidError(fields.serviceMessage?.value || '');
        }
        return;
      }
      if (result.outcome === RETRIEVAL_OUTCOMES.PREVENT_SALE) {
        if (fields.existingPlanPage?.value?.href) {
          navigate(fields.existingPlanPage.value.href);
        } else {
          setInvalidError(fields.serviceMessage?.value || '');
        }
        return;
      }
      if (
        result.outcome === RETRIEVAL_OUTCOMES.DEPENDANT_INVALID &&
        fields.whoToCoverPage?.value?.href
      ) {
        navigate(fields.whoToCoverPage.value.href);
        return;
      }

      // Successful retrieval (UNCHANGED, DATE_UPDATED, PREMIUM_CHANGED) -
      // close the drawer and send the user to the quote summary page.
      hideOverlay();
      if (fields.resultPage?.value?.href) {
        navigate(fields.resultPage.value.href);
      }
    } catch (populateError) {
      setInvalidError(fields.serviceMessage.value);
    }
  };

  const parseErrorDetails = field => {
    try {
      return JSON.parse(field?.value || '') || {};
    } catch {
      return {};
    }
  };

  const dispatchError = errorDetails => {
    const { uid, errorName, message } = errorDetails;
    dispatch(addErrorMessage({ uid, name: errorName, message }));
    setInvalidError(message);
  };

  const handleErrors = () => {
    for (const field in errors) {
      if (errors.hasOwnProperty(field) && errors[field]) {
        const errMsg = getValidationMessage(field);
        setInvalidError(errMsg || '');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = e => {
    e.preventDefault();
    setDisplayErrors(true);
    if (!handleErrors()) {
      return false;
    }
    const quoteNumberDetails = parseErrorDetails(fields.lookUpFailedQuoteNumber);
    const dobDetails = parseErrorDetails(fields.lookUpFailedDob);

    const { uid: quoteUidQuoteNo } = quoteNumberDetails;
    const { uid: quoteUidDob } = dobDetails;

    setDisplayErrors(true);

    const matchedError = errorMessages.find(
      err => err.uid === quoteUidQuoteNo || err.uid === quoteUidDob
    );

    setInvalidError(matchedError?.message || '');

    if (!retrieveQuoteNumber) {
      dispatchError(quoteNumberDetails);
      return;
    }

    if (!dateOfBirth) {
      dispatchError(dobDetails);
      return;
    }

    const baseUrl = config?.endpoints?.quotes;
    const retrieveUrl = new URL(baseUrl);
    const formattedDateOfBirth = dateOfBirth.split('T')[0];
    const params = new URLSearchParams({
      quoteNo: retrieveQuoteNumber,
      dateOfBirth: formattedDateOfBirth,
    });

    const proceedWithRequest = (token = '') => {
      params.append('recaptchaToken', token);
      retrieveUrl.search = params.toString();

      fetch(retrieveUrl.toString(), { method: 'GET' })
        .then(res => {
          if (!res.ok) {
            pushServiceDownError(retrieveUrl.toString(), res);
            setInvalidError(fields.failureMessage.value.trim());
            dispatch(showErrors());
            return {};
          }
          return res.json();
        })
        .then(async data => {
          if (Object.keys(data).length > 0) {
            await retrieveQuoteData(data);
          }
        })
        .catch(err => {
          pushServiceDownError(retrieveUrl.toString(), null, err);
          setInvalidError(fields.failureMessage.value.trim());
        });
    };

    if (matchedError) {
      return;
    }

    try {
      if (typeof window !== 'undefined' && config.recaptcha.toggle && window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(config.recaptcha.sitekey, { action: 'retrieve' })
            .then(proceedWithRequest);
        });
      } else {
        proceedWithRequest('');
      }
    } catch {
      setInvalidError(fields.serviceMessage.value);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div
      id="retrievequote-drawer"
      className="vds-slide-out-drawer vds-slide-out-drawer--active"
      aria-hidden="false"
      aria-expanded="true"
      data-testid="retrievequote-drawer"
    >
      <button
        type="button"
        className="vds-slide-out-drawer__background"
        onClick={hideOverlay}
        aria-label="Close drawer"
        data-testid="close-drawer-btn"
      ></button>

      <div className="vds-slide-out-drawer__inner">
        <div className="vds-slide-out-drawer__drawer">
          <button
            type="button"
            onClick={hideOverlay}
            className="vds-slide-out-drawer__close"
            aria-label="Close drawer"
            data-testid="close-drawer-btn-inner"
          ></button>

          {renderState()}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    ...props,
    show: state.appState?.overlay?.type === OverlayConstants.RetrieveQuote,
    retrieveQuoteNumber: state.appState.retrieveQuoteNumber,
    dateOfBirth: state.appState.retrieveQuoteDob,
    errorMessages: state.appState?.errorMessages,
  };
};
RetrieveQuote.propTypes = {
  fields: PropTypes.shape({
    testid: PropTypes.shape({ value: PropTypes.string }),
    formTitle: PropTypes.shape({ value: PropTypes.string }),
    submitButtonText: PropTypes.shape({ value: PropTypes.string }),
    introTitle: PropTypes.shape({ value: PropTypes.string }),
    introStrapLine: PropTypes.shape({ value: PropTypes.string }),
    introFooter: PropTypes.shape({ value: PropTypes.string }),
    contactNumber: PropTypes.shape({ value: PropTypes.string }),
    contactNumberText: PropTypes.shape({ value: PropTypes.string }),
    questions: PropTypes.arrayOf(PropTypes.shape({ fields: PropTypes.object })),
    serviceMessage: PropTypes.shape({ value: PropTypes.string }),
    failureMessage: PropTypes.shape({ value: PropTypes.string }),
    lookUpFailedQuoteNumber: PropTypes.shape({ value: PropTypes.string }),
    lookUpFailedDob: PropTypes.shape({ value: PropTypes.string }),
    resultPage: PropTypes.shape({ value: PropTypes.shape({ href: PropTypes.string }) }),
    serviceDownPage: PropTypes.shape({ value: PropTypes.shape({ href: PropTypes.string }) }),
    whoToCoverPage: PropTypes.shape({ value: PropTypes.shape({ href: PropTypes.string }) }),
  }).isRequired,
  show: PropTypes.bool,
  retrieveQuoteNumber: PropTypes.string,
  dateOfBirth: PropTypes.string,
  errorMessages: PropTypes.array,
  onError: PropTypes.func,
};

export default connect(mapStateToProps)(RetrieveQuote);
